import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { AppIcons } from 'app/modules/shared/app.icons';
import { Country } from 'app/modules/shared/models/country';
import { Language } from 'app/modules/shared/models/language';
import { CommonBindingDataService } from 'app/modules/shared/services/common-binding-data.service';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { MessageService } from 'primeng/api';
import { TabViewModule } from 'primeng/tabview';
import { MiFormComponent } from 'app/modules/shared/components/mi-form/mi-form.component';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { AppSettings } from 'app/modules/shared/app.settings';

@Component({
  selector: 'app-settings-notification-settings',
  standalone: true,
  imports: [InputTextModule, FormsModule, ReactiveFormsModule, TranslateModule, ButtonModule, MiFormComponent, TabViewModule, ToastModule, AccessProviderDirective, InputNumberModule, InputGroupModule, InputGroupAddonModule],
  templateUrl: './settings-notification-settings.component.html',
  styleUrl: './settings-notification-settings.component.scss'
})
export class SettingsNotificationSettingsComponent implements OnInit {
  @ViewChild('nameInput', { static: false }) nameInput!: ElementRef;
  miIcons = AppIcons;
  @ViewChild(MiFormComponent) form: MiFormComponent;
  attributeLabels = {};
  country: Country;
  language: Language;
  previousBtnLabel: any = this.cs.getLabel('lbl_cancel');
  saveNotificationSettings: any;
  activeIndex = 0;
  attributeValues: any;
  entityId: any;
  showForm: boolean = false;
  settingsNotificationData: any;
  notificationData: any;
  notificationEntityId: any;
  notificationSettingsEntityData: any;
  notificationAttributeData: any;
  notificationSettingsModuleName = AppSettings.ENTITY_TYPE.NOTIFICATION_SETTINGS;

  constructor(private configService: ConfigService,
    public cs: CommonBindingDataService,
    private messageService: MessageService,
    private entityService: EntityService,
    private fb: FormBuilder) {
  }

  ngOnInit() {
    this.updateButtonLabel();
    this.getNotificationSettings();
    this.setAppSettings();
    this.getAttributesForNotificationSetting();
    this.setNotificationEntityData();
  }

  updateButtonLabel() {
    this.saveNotificationSettings = this.entityId
      ? this.cs.getLabel('settings.lbl_update_general_settings')
      : this.cs.getLabel('settings.lbl_save_general_settings');
  }

  setNotificationEntityData() {
    this.notificationSettingsEntityData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getLoggedInTenantCode(),
      entityCode: AppSettings.ENTITY_TYPE.NOTIFICATION_SETTINGS
    }
  }

  getNotificationSettings() {
    this.entityService.getEntityDetailsByView(AppSettings.ENTITY_TYPE.NOTIFICATION_SETTINGS, {}).subscribe({
      next: (response: any) => {
        this.entityId = response.entityId;
        this.getEntityDetails();
        this.updateButtonLabel();
      },
      error: () => {
        this.showForm = true;
      }
    })
  }

  getAttributesForNotificationSetting() {
    this.entityService.getAttributeDefinition(AppSettings.ENTITY_TYPE.NOTIFICATION_SETTINGS, AppSettings.VIEW_CODE.ADD_EDIT_VIEW).subscribe(res => {
      if (res) {
        this.attributeLabels = this.cs.getAttributeLabels(res);
        this.notificationData = this.cs.getOrganizedAttribute(res);
      }
    });
  }

  getEntityDetails() {
    this.entityService.getSettingsEntity(this.entityId, AppSettings.ENTITY_TYPE.NOTIFICATION_SETTINGS, AppSettings.VIEW_CODE.ADD_EDIT_VIEW).subscribe({
      next: (response: any) => {
        this.attributeValues = this.cs.getOrgAttributeValues(response);
        this.showForm = true;
      },
      error: (error) => {
        this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].message) });
      }
    })
  }

  saveNoticationSettings(event) {
    const notificationEventData = event;
    if (this.entityId) {
      this.notificationEntityId = this.entityId;
      this.saveNotificationData(this.notificationSettingsEntityData.entityCode, notificationEventData, false);
    } else {
      this.entityService.createEntities(this.notificationSettingsEntityData.entityCode, this.notificationSettingsEntityData).subscribe({
        next: (res: any) => {
          this.notificationEntityId = res.entityId;
          this.saveNotificationData(this.notificationSettingsEntityData.entityCode, notificationEventData, true);
        },
        error: (error) => {
          this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].message) });
        }
      });
    }
  }

  saveNotificationData(entityCode, generalOrderManagementData, isNewEntity) {
    this.settingsNotificationData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_TYPE.NOTIFICATION_SETTINGS,
      countryCode: this.country[0].countryCode,
      languageCode: this.language[0].langCode,
      data: []
    };
    for (const [key, value] of Object.entries(generalOrderManagementData)) {
      const attributeValue = value;
      if (attributeValue) {
        this.settingsNotificationData.data.push({
          attributeCode: key,
          attributeValue
        });
      }
    }
    this.settingsNotificationData.data = this.cs.mapAttributeIds(this.settingsNotificationData.data, this.notificationData.tabs);
    this.entityService.saveAttributeData(entityCode, this.notificationEntityId, this.settingsNotificationData).subscribe({
      next: () => {
        if (isNewEntity) {
          this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('settings.lbl_notification_settings_saved_successfully') });
        } else {
          this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('settings.lbl_notification_settings_updated_successfully') });
        }
      },
      error: (error) => {
        this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].message) });
      }
    });
  }

  onCancel() {
    this.form.resetForm();
    this.attributeValues = null;
    if (this.entityId) {
      this.showForm = false;
      this.getEntityDetails();
    }
  }

  private setAppSettings() {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
  }

  onNextBtnClick() {
    const btn = document.getElementById('notification');
    btn.click();
  }

}
